import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/core/services/axios.js';

const LS_KEY = 'qp_perf_seen_ids';

function loadSeenIds() {
    try {
        return new Set(JSON.parse(localStorage.getItem(LS_KEY) || '[]'));
    } catch {
        return new Set();
    }
}

function saveSeenIds(set) {
    try {
        const arr = [...set];
        // храним не более 500 последних
        const trimmed = arr.slice(-500);
        localStorage.setItem(LS_KEY, JSON.stringify(trimmed));
    } catch { /* silent */ }
}

export const usePerformerSelectionStore = defineStore('performerSelection', () => {
    const queue = ref([]);
    const seenIds = ref(loadSeenIds());

    const current = computed(() => queue.value[0] ?? null);

    function enqueue(item) {
        if (!item.notificationId) return;
        if (seenIds.value.has(item.notificationId)) return;
        if (queue.value.some(i => i.notificationId === item.notificationId)) return;
        queue.value.push(item);
    }

    function hideFromQueue(notificationId) {
        seenIds.value.add(notificationId);
        saveSeenIds(seenIds.value);
        queue.value = queue.value.filter(i => i.notificationId !== notificationId);
    }

    function markSeen(notificationId) {
        hideFromQueue(notificationId);
        api.post(`/notifications/${notificationId}/read`).catch(() => {});
    }

    function dismissCurrent() {
        const item = current.value;
        if (item) hideFromQueue(item.notificationId);
    }

    function enqueueFromNotification(n) {
        if (n.payload?.action !== 'performer_selected' || !n.payload?.quick_post_id) return;
        enqueue({
            notificationId: n.id,
            quickPostId: n.payload.quick_post_id,
            ownerName: n.payload.owner_name || '',
            ownerPhone: n.payload.owner_phone || '',
            postTitle: n.payload.post_title || '',
        });
    }

    // Загружает офлайн-назначения. Возвращает массив, НЕ кладёт в очередь модалок —
    // вызывающий сам решает: редиректить первое, остальные enqueue.
    async function loadOffline() {
        try {
            const resp = await api.get('/notifications', {
                params: { limit: 50, unread_only: true },
            });
            const notifications = resp?.data?.data ?? [];
            const pending = [];
            for (const n of notifications) {
                if (n.payload?.action !== 'performer_selected' || !n.payload?.quick_post_id) continue;
                if (seenIds.value.has(n.id)) continue;
                pending.push({
                    notificationId: n.id,
                    quickPostId: n.payload.quick_post_id,
                    ownerName: n.payload.owner_name || '',
                    ownerPhone: n.payload.owner_phone || '',
                    postTitle: n.payload.post_title || '',
                });
            }
            return pending;
        } catch {
            return [];
        }
    }

    function clear() {
        queue.value = [];
        // seenIds не сбрасываем — они перманентны
    }

    return { queue, current, enqueue, enqueueFromNotification, markSeen, dismissCurrent, loadOffline, clear };
});
